import React, { useState, useEffect, useRef, FC } from "react"
import Styles from "../styles/VideoDrawModal.module.scss";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSlash } from '@fortawesome/free-solid-svg-icons'
import Image from "../components/image";
import GeneralButton from "../components/atoms/GeneralButton";
import useMoveObjectAndPinchToZoom from "../hooks/useMoveObjectAndPinchToZoom"
import { connect } from "react-redux";
import { AppState, setCommentFormFile, setCommentFormVisible } from "../state/app";
import SimpleActivityIndicator from "../components/atoms/SimpleActivityIndicator";
import { sleep } from "../functions/sleep"
import { WebViewVideoAction, WebViewVideoActionType } from "../types/WebViewVideoAction";
import clsx from "clsx"
import { isMouseEvent, isTouchEvent } from "../types/TouchOrMouseEvent";
import { CloseIcon } from "../icons/CloseIcon";

type vectorType = {
    x: number
    y: number
}


export function createBlobFromURL ( url: string ) {
    const blob = Base64toBlob( url ) as Blob & { image?: ImageData, name: string }
    // blob.lastModified = new Date();
    return blob
}

export function Base64toBlob ( base64: string ) {
    const tmp = base64.split( ',' );
    const data = atob( tmp[ 1 ] );
    const mime = tmp[ 0 ].split( ':' )[ 1 ].split( ';' )[ 0 ];
    const buf = new Uint8Array( data.length );
    for ( let i = 0; i < data.length; i++ ) {
        buf[ i ] = data.charCodeAt( i );
    }
    const blob = new Blob( [ buf ], { type: mime } );
    return blob;
}

const colorList = [
    "rgba(0,0,0,1)",
    "rgba(255,255,255,1)",
    "rgba(215, 35, 35, 1)",
    "rgba(35, 215, 35, 1)",
    "rgba(35, 35, 215, 1)"
]

const initialColorIndex = 2

const methodList = [ {
    name: "free",
    icon: null
}, {
    name: "line",
    icon: faSlash
} ]


const canvasScale = 2

type Props = {
    video: HTMLVideoElement,
    videoScale: number,
    closeModal: () => void,
    commentFormFile: File | null,
    setCommentFormFile: ( file: File | null ) => void,
    setCommentFormVisible: ( bool: boolean ) => void,
    webViewDispatch: ( action: WebViewVideoActionType ) => void,
    forWebView: boolean
}

const VideoDrawModal: FC<Props> = ( {
    video,
    videoScale,
    closeModal,
    commentFormFile,
    setCommentFormFile,
    setCommentFormVisible,
    webViewDispatch = () => { },
    forWebView = false,
} ) => {

    const [ scale, setScale ] = useState( 1 )
    const [ method, setMethod ] = useState( "free" )
    const [ color, setColor ] = useState( colorList[ initialColorIndex ] )
    const [ historyImageList, setHistoryImageList ] = useState<ImageData[]>( [] )
    const [ historyIndex, setHistoryIndex ] = useState<number>( 0 )
    const [ moveMode, setMoveMode ] = useState( false )
    const [ lineWidth, setLineWidth ] = useState( 5 )
    const [ lineColorDepth, setLineColorDepth ] = useState( 18 )
    const [ drawing, setDrawing ] = useState( false )
    const [ startImageForLine, setStartImageForLine ] = useState<ImageData | null>( null )
    const [ startDrawCoordinates, setStartDrawCoordinates ] = useState<vectorType | null>( null )
    const [ canvasImageURL, setCanvasImageURL ] = useState( "" )
    const [ canvasImageVisible, setCanvasImageVisible ] = useState( false )
    const [ drawEventAddedElement, setDrawEventAddedElement ] = useState<HTMLDivElement | null>( null )
    const [ attatchingCanvasImageToCommentForm, setAttatchingCanvasImageToCommentForm ] = useState( false )


    const canvas = useRef<HTMLCanvasElement>( null )
    const ctxRef = useRef<CanvasRenderingContext2D | null>( null )
    const canvasContainer = useRef<HTMLDivElement>( null )
    const drawingRef = useRef( drawing )
    const methodRef = useRef( method )
    const colorRef = useRef( color )
    const lineWidthRef = useRef( lineWidth )
    const lineColorDepthRef = useRef( lineColorDepth / 20 )
    const startImageForLineRef = useRef( startImageForLine )
    const startDrawCoordinatesRef = useRef( startDrawCoordinates )
    const historyImageListRef = useRef( historyImageList )
    const historyIndexRef = useRef( historyIndex )
    const moveModeRef = useRef( moveMode )
    const scaleRef = useRef( scale )

    const fixBackground = () => {
        document.getElementsByTagName( "html" )[ 0 ].style.overflowY = "hidden"
        document.getElementsByTagName( "body" )[ 0 ].style.overflowY = "hidden"
        // const layout = document.getElementById( "clubcloud_layout" )
        // if ( layout ) layout.style.pointerEvents = "none"
    }
    const releaseBackground = () => {
        document.getElementsByTagName( "html" )[ 0 ].style.overflowY = "auto"
        document.getElementsByTagName( "body" )[ 0 ].style.overflowY = "auto"
        // const layout = document.getElementById( "clubcloud_layout" )
        // if ( layout ) layout.style.pointerEvents = "auto"
    }

    useEffect( () => {
        fixBackground()
        webViewDispatch( WebViewVideoAction.START_DRAW )
        setTimeout( () => {
            initCanvas()
            addEventListner()
            saveInitialImageToHistory()
            forWebView || putCommentFormToBottom()
        }, forWebView ? 100 : 0 )

        return () => {
            removeEventListener()
            releaseBackground()
            forWebView || putCommentFormOnFooter()
        }
    }, [] )

    const putCommentFormToBottom = () => {
        const commentForm = document.getElementById( "comment_form" )
        if ( commentForm ) commentForm.style.bottom = "0"
        const commentFormButton = document.getElementById( "comment_form_button" )
        if ( commentFormButton ) commentFormButton.classList.add( "small_comment_form_button" )
    }

    const putCommentFormOnFooter = () => {
        const commentForm = document.getElementById( "comment_form" )
        if ( commentForm ) commentForm.style.removeProperty( 'bottom' )
        const commentFormButton = document.getElementById( "comment_form_button" )
        if ( commentFormButton ) commentFormButton.classList.remove( "small_comment_form_button" )
    }

    useEffect( () => {
        if ( scale < 0.1 ) {
            setScale( 0.1 )
            return
        }
        scaleRef.current = scale
    }, [ scale ] )

    useEffect( () => {
        moveModeRef.current = moveMode
        moveMode ? removeEventListener() : addEventListner()
    }, [ moveMode ] )

    useEffect( () => {
        drawingRef.current = drawing;
    }, [ drawing ] );

    useEffect( () => {
        methodRef.current = method;
    }, [ method ] );

    useEffect( () => {
        colorRef.current = color;
    }, [ color ] );

    useEffect( () => {
        lineWidthRef.current = lineWidth / scaleRef.current;
    }, [ lineWidth ] );

    useEffect( () => {
        if ( !canvas.current ) return
        ctxRef.current = canvas.current.getContext( '2d' )
    }, [ canvas ] )

    useEffect( () => {
        removeEventListener()
        addEventListner()
    }, [ canvas ] )

    useEffect( () => {
        startImageForLineRef.current = startImageForLine
    }, [ startImageForLine ] )

    useEffect( () => {
        startDrawCoordinatesRef.current = startDrawCoordinates
    }, [ startDrawCoordinates ] )

    useEffect( () => {
        lineColorDepthRef.current = lineColorDepth / 20
    }, [ lineColorDepth ] )


    useEffect( () => {
        historyImageListRef.current = historyImageList
    }, [ historyImageList ] )

    useEffect( () => {
        historyIndexRef.current = historyIndex
    }, [ historyIndex ] )

    const initCanvas = () => {
        if ( !canvas.current ) return
        const videoHeight = video.getBoundingClientRect().height / videoScale
        const videoWidth = video.getBoundingClientRect().width / videoScale
        const scaledVideoHeight = videoHeight * canvasScale
        const scaledVideoWidth = videoWidth * canvasScale
        canvas.current.setAttribute( 'height', `${ scaledVideoHeight }px` )
        canvas.current.setAttribute( 'width', `${ scaledVideoWidth }px` )
        const ctx = canvas.current.getContext( '2d' )
        if ( !ctx ) return
        ctx.drawImage( video, 0, 0, scaledVideoWidth, scaledVideoHeight )
        console.log( "videoHeight", videoHeight )
        console.log( "realHeight", canvas.current.getBoundingClientRect().height )

        if ( forWebView ) canvas.current.style.maxHeight = "50vh"
        if ( forWebView ) canvas.current.style.maxWidth = `${ document.documentElement.getBoundingClientRect().width + 16 }px`
        if ( forWebView ) canvas.current.style.opacity = "0.5"

        setTimeout( () => {
            if ( !canvas.current ) return
            const canvasRealWidth = canvas.current.getBoundingClientRect().width
            const canvasRealHeight = canvas.current.getBoundingClientRect().height
            const widthRatio = videoWidth / canvasRealWidth
            const heightRatio = videoHeight / canvasRealHeight
            const ratio = widthRatio
            ctx.scale( canvasScale * ratio, canvasScale * ratio )
            canvas.current.style.opacity = "1"
        }, 1000 ) // appと密結合注意
        ctx.strokeStyle = 'rgba(255, 0 , 0, 1)'
        ctx.lineWidth = 10
    }

    const addEventListner = () => {
        const canvasContainer__ = canvasContainer.current
        if ( !canvasContainer__ ) return
        setDrawEventAddedElement( canvasContainer__ )
        canvasContainer__.addEventListener( "touchstart", startDrawing )
        canvasContainer__.addEventListener( "mousedown", startDrawing )
        canvasContainer__.addEventListener( "touchmove", draw, { passive: false } )
        canvasContainer__.addEventListener( "mousemove", draw, { passive: false } )
        document.addEventListener( "touchend", endDrawing )
        document.addEventListener( "mouseup", endDrawing )
    }

    const removeEventListener = () => {
        const canvasContainer__ = drawEventAddedElement
        if ( !canvasContainer__ ) return

        canvasContainer__.removeEventListener( "touchstart", startDrawing )
        document.removeEventListener( "touchstart", startDrawing )

        canvasContainer__.removeEventListener( "mousedown", startDrawing )
        document.removeEventListener( "mousedown", startDrawing )

        const opts: AddEventListenerOptions & EventListenerOptions = { passive: true };
        canvasContainer__.removeEventListener( "touchmove", draw, opts )
        document.removeEventListener( "touchmove", draw, opts )

        canvasContainer__.removeEventListener( "touchend", endDrawing )
        document.removeEventListener( "touchend", endDrawing )

        canvasContainer__.removeEventListener( "mouseup", endDrawing )
        document.removeEventListener( "mouseup", endDrawing )
    }

    const saveInitialImageToHistory = () => {
        if ( !canvas.current ) return
        const ctx = canvas.current.getContext( '2d' )
        if ( !ctx ) return
        const initialImage = ctx.getImageData( 0, 0, canvas.current.width * scaleRef.current, canvas.current.height * scaleRef.current )
        setHistoryImageList( [ initialImage ] )
        setHistoryIndex( 0 )
    }

    const startDrawing = ( e: TouchEvent | MouseEvent ) => {
        if ( moveModeRef.current ) return
        if ( !canvas.current ) return
        const ctx = canvas.current.getContext( '2d' )
        if ( !ctx ) return
        const x = getCoordinates( e ).x
        const y = getCoordinates( e ).y
        ctx.beginPath()
        ctx.moveTo( x, y )
        setStartDrawCoordinates( { x, y } )
        setStartImageForLine( ctx.getImageData( 0, 0, canvas.current.width, canvas.current.height ) )
        ctx.strokeStyle = colorRef.current
        ctx.lineWidth = lineWidthRef.current
        ctx.globalAlpha = lineColorDepthRef.current
        if ( methodRef.current === "line" ) {
            setStartImageForLine( ctx.getImageData( 0, 0, canvas.current.width, canvas.current.height ) )
        }

        // if(method=="free" && draw){
        if ( methodRef.current === "free" ) {
            ctx.globalAlpha = lineColorDepthRef.current * 1 / 3
            // ctx.strokeStyle = changeOpacityForFree(ctx.strokeStyle, start_or_end="start")
        }
        setDrawing( true )
    }

    const draw = ( e: TouchEvent | MouseEvent ) => {
        if ( moveModeRef.current ) return
        e.preventDefault()
        if ( !drawingRef.current ) return
        if ( !canvas.current ) return
        const ctx = canvas.current.getContext( '2d' )
        if ( !ctx ) return
        const ctxGlobalAlfa = ctx.globalAlpha
        if ( methodRef.current === "line" ) {
            startImageForLineRef.current && ctx.putImageData( startImageForLineRef.current, 0, 0 )
            ctx.globalAlpha = ctxGlobalAlfa
            ctx.beginPath();
            // ctx.moveTo(startDrawCoordinatesRef.current.x ,startDrawCoordinatesRef.current.y)
            startDrawCoordinatesRef.current && ctx.moveTo( startDrawCoordinatesRef.current.x, startDrawCoordinatesRef.current.y )
            ctx.lineTo( getCoordinates( e ).x, getCoordinates( e ).y )
            ctx.stroke()
            ctx.globalAlpha = ctxGlobalAlfa
        }
        else if ( methodRef.current === "free" ) {
            ctx.globalAlpha = lineColorDepthRef.current * 1 / 3
            ctx.lineTo( getCoordinates( e ).x, getCoordinates( e ).y )
            ctx.stroke()
        }
    }

    const endDrawing = ( e: TouchEvent | MouseEvent ) => {
        if ( moveModeRef.current ) return
        if ( !drawingRef.current ) return
        if ( !canvas.current ) return
        const ctx = canvas.current.getContext( '2d' )
        if ( !ctx ) return
        setDrawing( false )

        ctx.globalAlpha = ctx.globalAlpha * 1.5 //これいらないかも
        const newHistoryImageList = historyImageListRef.current.slice( 0, historyIndexRef.current + 1 )
        // let nowImage = ctx.getImageData(0, 0, canvas.current.width*scaleRef.current, canvas.current.height*scaleRef.current)
        const nowImage = ctx.getImageData( 0, 0, canvas.current.width, canvas.current.height )
        newHistoryImageList.push( nowImage )
        setHistoryImageList( newHistoryImageList )
        setHistoryIndex( newHistoryImageList.length - 1 )
        if ( method === "free" ) {
            ctx.globalAlpha = lineColorDepthRef.current * 3
            ctx.beginPath()
            // ctx.strokeStyle = changeOpacityForFree(ctx.strokeStyle, start_or_end="end")
        }
        else if ( method === "line" ) {
            //新しいパスを開始する
            // ctx.putImageData(startDrawCoordinatesRef.current, 0, 0)
            startImageForLineRef.current && ctx.putImageData( startImageForLineRef.current, 0, 0 )
            ctx.beginPath()
            ctx.lineCap = "round"
            startDrawCoordinatesRef.current && ctx.moveTo( startDrawCoordinatesRef.current.x, startDrawCoordinatesRef.current.y )
            ctx.lineTo( getCoordinates( e ).x, getCoordinates( e ).y )
            ctx.stroke()
        }
        // console.log("now", lineColorDepthRef.current)
        // canvas.current.getContext('2d').globalAlpha = lineColorDepthRef.current

    }

    //デバイス内の絶対座標(タッチ位置)をcanvasない座標に変換 ただし、scaleも考える
    const getCoordinates = ( e: TouchEvent | MouseEvent ) => {

        let X
        let Y

        if ( isTouchEvent( e ) ) {
            if ( e.type === "touchstart" || e.type === "touchmove" ) {
                X = e.touches[ 0 ].clientX
                Y = e.touches[ 0 ].clientY
            }
            else if ( e.type === "touchend" ) {
                X = e.changedTouches[ 0 ].clientX
                Y = e.changedTouches[ 0 ].clientY
            }
            else {
                X = 0
                Y = 0
            }
        }
        else if ( isMouseEvent( e ) ) {
            X = e.clientX
            Y = e.clientY
        }
        else {
            X = 0
            Y = 0
        }

        let left
        let top
        if ( !canvas.current ) {
            alert( "エラーが発生しました" )
            left = 0
            top = 0
        }
        else {
            left = canvas.current.getBoundingClientRect().left
            top = canvas.current.getBoundingClientRect().top
        }
        return { x: ( X - left ) / scaleRef.current, y: ( Y - top ) / scaleRef.current }
    }

    const onChangeRangeValue = ( name: string, value: number ) =>
        name === "width" ?
            setLineWidth( value ) :
            setLineColorDepth( value )

    const renderMethodButtonList = () =>
        methodList.map( method__ =>
            <button
                onClick={ () => setMethod( method__.name ) }
                className={ method === method__.name ? Styles.selected : "" }>
                { method__.icon ?
                    <FontAwesomeIcon style={ { height: "1.25rem" } } icon={ method__.icon } /> :
                    <Image style={ { height: "1.25rem", width: "1.25rem" } } filename="free_hand.jpg" /> }
            </button>
        )

    const undo = () => {
        if ( !isUndoActive() ) { return }
        const history = historyImageListRef.current
        const index = historyIndexRef.current
        if ( !index ) return
        if ( !canvas.current ) return
        const ctx = canvas.current.getContext( '2d' )
        if ( !ctx ) return
        const previousImage = history[ index - 1 ]
        ctx.putImageData( previousImage, 0, 0 )
        setHistoryIndex( index - 1 )
        // setHistoryImageList(history.slice(0,historyIndex-1))
    }

    const redo = () => {
        if ( !isRedoActive() ) { return }
        const history = historyImageListRef.current
        const index = historyIndexRef.current
        if ( index === null ) return
        if ( !canvas.current ) return
        const ctx = canvas.current.getContext( '2d' )
        if ( !ctx ) return
        const nextImage = history[ index + 1 ]
        ctx.putImageData( nextImage, 0, 0 )
        setHistoryIndex( index + 1 )
        // setHistoryImageList(history.concat(nextImage).slice())
    }

    //戻す
    //indexが一番最後じゃなければOK
    const isUndoActive = () => {
        return historyIndex !== null && historyIndex !== 0
    }

    //進む
    //indexが0じゃなければOK
    const isRedoActive = () => {
        return historyIndex !== null && historyIndex !== historyImageList.length - 1
    }

    const attatchCanvasImageToComment = async () => {

        if ( !forWebView && commentFormFile ) {
            if ( window.confirm( "すでにファイルが添付されています\n上書きしますか？" ) ) {
                const blob = getCanvasImage()
                setCommentFormFile( blob )
                window.commentFormFile = blob
                if ( !canvas.current ) return
                window.canvasImageURL = canvas.current.toDataURL( "image/jpeg" )
                webViewDispatch( WebViewVideoAction.ADD_IMAGE_TO_COMMENT )
            }
        }
        else {
            setAttatchingCanvasImageToCommentForm( true )
            await sleep( 100 )
            sendImageToApp()
        }
    }

    const sendImageToApp = () => {
        const blob = getCanvasImage()
        setCommentFormFile( blob )
        window.commentFormFile = blob
        if ( !canvas.current ) return
        window.canvasImageURL = canvas.current.toDataURL( "image/jpeg" )
        webViewDispatch( WebViewVideoAction.ADD_IMAGE_TO_COMMENT )
        setTimeout( () => setAttatchingCanvasImageToCommentForm( false ), 300 )
    }

    const getCanvasImage = () => {
        if ( !canvas.current ) return null
        const ctx = canvas.current.getContext( '2d' )
        if ( !ctx ) return null
        const canvasImageURL = canvas.current.toDataURL( "image/jpeg" )
        const blob = createBlobFromURL( canvasImageURL )
        blob.name = video.src + "screenshot.jpg";
        blob.image = ctx.getImageData( 0, 0, canvas.current.width * scaleRef.current, canvas.current.height * scaleRef.current )
        return blob as File
    }

    const displayCanvasImage = () => {
        if ( !canvas.current ) return
        setCanvasImageURL( canvas.current.toDataURL( "image/jpeg" ) )
        setCanvasImageVisible( true )
    }

    const downloadCanvasImage = () => {
        if ( !canvas.current ) return
        window.canvasImageURL = canvas.current.toDataURL( "image/jpeg" )
        window.canvasImageFile = createBlobFromURL( window.canvasImageURL )
        webViewDispatch( WebViewVideoAction.DOWNLOAD_CANVAS_IMAGE )
    }

    const confirmCloseModal = () => {
        if ( historyImageListRef.current.length === 1 ) {
            closeModal()
            webViewDispatch( WebViewVideoAction.END_DRAW )
        }
        else if ( window.confirm( "編集を破棄しますか？" ) ) {
            closeModal()
            webViewDispatch( WebViewVideoAction.END_DRAW )
        }
    }


    const renderColorButtonList = () =>
        colorList.map( color__ => (
            <button
                style={ { backgroundColor: color__ } }
                onClick={ () => setColor( color__ ) }
                className={ color === color__ ? Styles.selected : "" }>
            </button>
        ) )


    const renderCanvasMoveModal = () =>
        <div className={ Styles.canvas_move_modal_content }>
            <div
                onClick={ () => setMoveMode( false ) }
                className={ Styles.close_icon_wrapper }
                style={ { color: "#fff", fontSize: "1.6rem" } }>
                <CloseIcon />
            </div>
            <p style={ { marginBottom: 16 } }>画像を拡大すると移動可能になります</p>
        </div>

    const u = useMoveObjectAndPinchToZoom(
        canvas.current,
        false,
        moveMode,
        setScale,
        false )

    const renderDraw = () => {
        return (
            <div className={ `${ Styles.container }` } id="canvas_background" onClick={ () => setCommentFormVisible( false ) }>
                <div
                    onClick={ () => confirmCloseModal() }
                    className={ Styles.close_icon_wrapper }
                    style={ { color: "#fff", fontSize: "1.6rem" } }>
                    <CloseIcon />
                </div>
                <div className={ Styles.canvas_container }
                    ref={ canvasContainer }>
                    <canvas
                        ref={ canvas }
                        { ...u }
                        id="canvas"
                        className={ `video_screenshot ${ moveMode ? Styles.move_mode_canvas : "" }` }
                    >
                    </canvas>
                </div>
                <div className={ `${ Styles.controller_and_buttons } ${ forWebView ? Styles.for_webview_controller_and_buttons : "" }` }>
                    <div className={ Styles.canvas_move_mode_switch_button_container }>
                        <GeneralButton
                            title={ moveMode ? "描画モードへ切り替え" : "画像の移動/拡大/縮小モードへ切り替え" }
                            onClick={ () => setMoveMode( !moveMode ) }
                            className={ Styles.toggle_draw_mode_button }
                            style={ moveMode ? { backgroundColor: "rgb(255,84,28)" } : {} } />
                    </div>

                    <div className={ `${ Styles.draw_controller } ${ moveMode ? Styles.draw_inactive_cover : "" }` }>
                        <div className={ Styles.draw_button_container }>
                            <div className={ Styles.method_container }>
                                { renderMethodButtonList() }
                            </div>
                            <div className={ Styles.color_button_container }>
                                { renderColorButtonList() }
                            </div>
                            <div className={ Styles.redo_undo_container }>
                                <button
                                    onClick={ () => undo() }
                                    className={ clsx( isUndoActive() && Styles.active ) }>戻る</button>
                                <button
                                    onClick={ () => redo() }
                                    className={ clsx( isRedoActive() && Styles.active ) }>進む</button>
                            </div>
                            { moveMode &&
                                <div className={ Styles.canvas_move_modal }>
                                    { renderCanvasMoveModal() }
                                </div>
                            }
                        </div>
                        <div className={ Styles.draw_style_container }>
                            <div className={ Styles.style_range_wrapper }>
                                <div>
                                    <span>線の太さ</span>
                                    <span className={ Styles.range_value }>{ lineWidth }</span>
                                </div>
                                <input
                                    onChange={ e => onChangeRangeValue( 'width', Number( e.target.value ) ) }
                                    type="range" min="1" max="20" value={ String( lineWidth ) } />
                            </div>
                            <div className={ Styles.style_range_wrapper }>
                                <div>
                                    <span>線の濃さ</span>
                                    <span className={ Styles.range_value }>{ lineColorDepth }</span>
                                </div>
                                <input
                                    onChange={ e => onChangeRangeValue( 'colorDepth', Number( e.target.value ) ) }
                                    type="range" min="1" max="20" value={ String( lineColorDepth ) } />
                            </div>
                        </div>
                    </div>

                    <div className={ Styles.button_container }>
                        <button onClick={ () => attatchCanvasImageToComment() } onTouchStart={ () => { } } key="1">
                            コメントに添付
                            { attatchingCanvasImageToCommentForm &&
                                <SimpleActivityIndicator style={ { display: "inline-block", marginLeft: 8 } } /> }
                        </button>
                        <button onClick={ () => forWebView ? downloadCanvasImage() : displayCanvasImage() } onTouchStart={ () => { } } key="2">
                            { forWebView ? "画像を端末に保存" : "画像として表示" }
                        </button>
                    </div>
                </div>
                { canvasImageVisible &&
                    <div className={ `${ Styles.container }` }>
                        <img src={ canvasImageURL } className={ Styles.image_for_download } />
                        <div
                            onClick={ () => setCanvasImageVisible( false ) }
                            // className={Styles.close_icon_wrapper} 
                            style={ { color: "#fff", fontSize: "1.6rem", cursor: "pointer" } }>
                            <span>画像を閉じる</span>
                        </div>
                    </div> }
            </div>
        )
    }

    return (
        renderDraw()
    )
}

const mapStateToProps = ( state: AppState ) => ( {
    commentFormFile: state.app.commentFormFile,
    commentFormVisible: state.app.commentFormVisible,
} )

const mapDispatchToProps = {
    setCommentFormFile,
    setCommentFormVisible
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)( VideoDrawModal )
